// 2026-05-26 - FormuLearning - Step 4 API 호출
// 2026-07-08 수정 - F-10(이슈 #21): KPI·Expected f2·처방 지시서 타입 추가
// 2026-08-30 수정 - 이슈 #58 S1(#64): `current_round_id`·`formulation_prefix` 수용.
// 2026-09-01 수정 - 이슈 #58 S10 3차(#70, M84-36·69): `completeOptimization` 이 **최종
//                   처방을 싣고 간다** · `finalPrescriptionUrl()` 신설(전 라운드 정리본).
// 2026-09-23 수정 - 이슈 #67 S4-4: 추천이 **작업 등록**(202 `{job, excluded}`)으로 바뀐다 — 진행은
//                   `streamOptimizationJob`(SSE, fetch 스트림)·`getOptimizationJob` 으로 본다. 폐기 필드
//                   (KPI 5종·배치 신뢰도·acquisition·predicted_mean/std) 제거, `predicted` 신설(인터페이스 v1.14 §2.6).
// 2026-09-23 수정 - 이슈 #67 S4-7: 분석 조회 `getStep4Analysis`·`Step4Analysis` 신설(인터페이스 v1.16 §2.6) —
//                   성분 중요도·추천 카드 예측 그래프의 원천.
// 2026-09-23 수정 - 이슈 #83: 추천 작업 중단 `cancelOptimizationJob`(Break) — 대기·진행 중만 닫고, 반영 중이면 `applying`.
// 2026-09-24 수정 - 이슈 #76(G5, M901-29): 추천 요청에 격자 계산 제외 2필드(`excludedTimePoints`·`excludedSolutions`).
// 2026-09-26 수정 - 이슈 #95 단계 3-5: `uploadStep4Results`·`dissolutionTemplateUrl` 삭제(`M84-75` 업로드·양식 폐기).
// 파일 위치: frontend/src/api/step4.ts

import { isAxiosError } from 'axios'
import client from './client'
import type { ApiResponse } from '../types/api'
import type { DissolutionProfile } from './step3'
import type { RoundInfo } from './round'

/** 마지막으로 쓴 추천 개수만 남는다 — ~~acquisition~~ 폐기(탐색 설정은 작업 입력이 기록, BR-OPT-07). */
export interface OptimizationConfig {
  n_recommendations: number
}

export interface Step4Run {
  id: string
  /** 구 정수 회차 자리 — `round.round_no` 어댑터(인터페이스 §2.10.2). */
  run_id: number
  /** 소속 라운드 UUID(#58 S1). 신규 개발은 이 필드를 정본으로 본다. */
  round_id: string | null
  /** 처방 식별자 `AMBO-NN`(BR-RND-01). 백엔드 `opti_dict` 가 항상 실어 보낸다. */
  batch_code: string
  batch_type: "init" | "opti"
  recommended_x: Record<string, number>
  actual_x: Record<string, number>
  y_values: Record<string, number>
  dissolution_profiles: DissolutionProfile[]
  // 2026-07-20 - 이슈 #42: f2는 시스템 계산값(R1). 제외 배치는 null(BR-S4-L10).
  f2: number | null
  f2_by_solution: Record<string, number>
  f2_reason: string | null
  /** 목적함수별 예측(원 단위, 추천 시점 고정, BR-OPT-14). opti 만. 이관분은 `_source` 메타 키를 단다. */
  predicted: Record<string, { mean: number; std: number } | string> | null
  /** 처방별 AI 해설(BR-OPT-15) — S4-6 부터 채워진다. */
  ai_commentary_ko: string | null
  is_excluded: boolean
  status: 'recommended' | 'uploaded' | 'excluded'
}

export interface Step4TargetKpi {
  response: string
  direction: string
  value: number | null
  best: number | null
  achieved: boolean
}

export interface Step4F2Point {
  iteration: number
  f2: number
  phase: 'lhs' | 'bo'
}

/** 존치 2종 — ~~current_iteration·best_f2·model_confidence·uncertainty_reduced·expected_f2·acquisition~~
 *  폐기(M84-35 · BR-OPT-14, 인터페이스 v1.14 §2.6). */
export interface Step4Kpi {
  target: Step4TargetKpi
  f2_series: Step4F2Point[]
}

/** 최적화 작업 요약(인터페이스 v1.14 §2.6 `OptimizationJobSummary`). */
export interface OptimizationJobSummary {
  id: string
  job_type: 'suggest' | 'analyze'
  status: 'queued' | 'running' | 'succeeded' | 'failed' | 'cancelled'
  stage: 'load' | 'fit' | 'search' | 'profile' | 'analyze' | 'write' | 'ai_summary' | 'apply' | null
  created_at: string
  started_at: string | null
  finished_at: string | null
  applied_at: string | null
  round_id: string | null
  error: { code: string; message: string } | null
  timings: Record<string, number>
}

/** 학습에서 뺀 배치와 사유(BR-OPT-05). 사유 어휘는 인터페이스 §4.3. */
export interface ExcludedBatch {
  batch_id: string
  batch_code: string
  reason: string
  f2_reason?: string
}

/** 라운드별 누적 HV — optimizer `analyze_hv`. 달성률 = 그 라운드 HV ÷ 지금까지 최고 HV × 100. */
export interface HvProgressRound {
  round_no: number
  /** 그 라운드까지 누적 관측 수. */
  n: number
  hv: number
  achievement_pct: number
  /** 직전 라운드 대비 달성률 상승(%p). 첫 라운드는 null. */
  gain_pp: number | null
}

export interface HvProgress {
  rounds: HvProgressRound[]
  hv_max: number
  hv_now: number
  reference_point: Record<string, number>
  /** 마지막 상승 폭이 `threshold_pp` 미만. 라운드가 하나면 false. */
  converged: boolean
  /** 수렴 안내 기준(%p) — 백엔드가 등록 시 설정에서 붙인다. */
  threshold_pp: number | null
}

/**
 * 분석 결과(인터페이스 v1.16 §2.6 `Step4Analysis`) — 마지막으로 **성공한** 분석 작업이다. 조회 시점에 계산하지 않는다.
 * 성분 중요도·진척도는 전체 누적 관측(BR-OPT-17)이라 라운드 선택과 무관하게 같은 값이다.
 */
export interface Step4Analysis {
  job_id: string
  /** 분석 등록 시각 = 기록의 «시점». 화면 기준 시각이 이것이다. */
  created_at: string
  finished_at: string
  basis_round_id: string | null
  /** `{목적함수: {변수: %}}` — 목적함수마다 합 100, 공정변수 포함. */
  importance: Record<string, Record<string, number>>
  /** HV 진척도 — `HvProgressChart`(잠정, `Q-260917-03`). 용액별 f2 추이(`BatchF2Chart`)와 병존한다. */
  progress: HvProgress
  /** `{batch_id: {용액키: [{time, mean, std}]}}` — optimization 배치 추천 조성의 예측 용출(%). */
  profiles: Record<string, Record<string, { time: number; mean: number; std: number }[]>>
  excluded: ExcludedBatch[]
  profile_error?: string
}

/** `GET …/analysis` 응답 — 분석 · 더 새 분석의 대기·진행 여부 · 그 뒤 실패(BR-OPT-16). */
export interface Step4AnalysisView {
  /** optimization round 가 없으면 null. */
  analysis: Step4Analysis | null
  /** 마지막 성공보다 새 분석이 대기·진행 중 — 값은 그대로 두고 «재계산 중» 을 단다. */
  recalculating: boolean
  last_failed: { job_id: string; error: { code?: string; message?: string } } | null
}

/** 추천 등록 응답(202) — 계산은 optimizer 가 하고, 라운드는 성공 뒤 반영에서 생긴다(BR-OPT-12). */
export interface OptimizeAccepted {
  job: OptimizationJobSummary
  excluded: ExcludedBatch[]
}

/** 작업 조회 — 반영이 끝난 suggest 에만 `round`·`batches` 가 실린다. */
export interface OptimizationJobView extends OptimizationJobSummary {
  request: Record<string, unknown>
  excluded: ExcludedBatch[]
  round?: RoundInfo | null
  batches?: Step4Run[]
}

/** 스트림 이벤트 — Step 1 `search/stream` 과 같은 프레이밍 `{event, data}`. */
export type OptimizationStreamEvent =
  | { event: 'stage'; data: { stage: OptimizationJobSummary['stage']; elapsed_sec: number } }
  | { event: 'done'; data: { job: OptimizationJobSummary; round?: RoundInfo | null; batches?: Step4Run[]; excluded?: ExcludedBatch[] } }
  | { event: 'error'; data: { job: OptimizationJobSummary | null; message?: string } }

/** `optimize` 422·409 · `complete` 409 의 `detail` 객체(인터페이스 v1.14 §2.6). */
export interface OptimizeErrorDetail {
  code: string
  message: string
  job_id?: string
  excluded?: ExcludedBatch[]
  [key: string]: unknown
}

export interface Step4Data {
  id: string
  project_id: string
  /** 화면이 보고 있는 라운드(#58 S1). 구 정수 회차는 아래 어댑터 값으로만 남는다. */
  current_round_id: string | null
  /** 처방 명명 접두어(BR-RND-01). 미설정이면 서버 잠정값 `FM`이 쓰인다. */
  formulation_prefix: string | null
  /** 최종 처방(배치) — 완료 시 확정된다(M84-36). 완료 전에는 `null`. */
  final_batch_id: string | null
  /** 구 정수 회차 자리 — `round.round_no` 어댑터(인터페이스 §2.10.2). */
  current_run_id: number
  is_completed: boolean
  completed_at: string | null
  optimization_config: OptimizationConfig
  current_runs: Step4Run[]
  kpi: Step4Kpi
  /** 대기·진행·반영 중 작업 — 새로고침 뒤 진행 표시를 잇는다(BR-OPT-13). */
  active_jobs: OptimizationJobSummary[]
  created_at: string
  updated_at: string
}

export function prescriptionUrl(projectId: string): string {
  return `/api/v1/step4/${projectId}/prescription`
}

/**
 * 최종 처방표(M84-69) — **전 라운드 정리본**. 라운드별은 `prescriptionUrl()` 이다.
 *
 * `prescriptionUrl()` 과 달리 `/api/v1` 접두어를 두지 않는다 — `downloadFile()` 이
 * axios 클라이언트를 쓰므로 baseURL 이 이미 붙는다. 기존 함수는 호출부가
 * `.replace('/api/v1', '')` 로 벗겨 쓰고 있어 그대로 둔다(소비처가 둘이라 지금 고치면
 * 3차 범위 밖의 회귀를 부른다).
 */
export function finalPrescriptionUrl(projectId: string): string {
  return `/step4/${projectId}/final-prescription`
}

export function dissolutionProfilesUrl(projectId: string): string {
  return `/step4/${projectId}/dissolution-profiles`
}

/** 인증이 필요한 파일 다운로드 — <a href>는 Bearer가 붙지 않아 401이 나므로 blob으로 받는다. */
export async function downloadFile(url: string, filename: string): Promise<void> {
  const { data } = await client.get<Blob>(url, { responseType: 'blob' })
  const blobUrl = URL.createObjectURL(data)
  const a = document.createElement('a')
  a.href = blobUrl
  a.download = filename
  a.click()
  URL.revokeObjectURL(blobUrl)
}

export async function getStep4(projectId: string): Promise<Step4Data> {
  const { data } = await client.get<ApiResponse<Step4Data>>(`/step4/${projectId}/`)
  return data.data!
}

// 2026-08-03 - 이슈 #50 P6: `getStep4History` 제거. #43에서 회차 드롭다운(RunHistorySection)이
// 통합표 필터로 흡수되며 호출부가 사라졌다. 서버 `GET /step4/{pid}/history` 도 #95 에서 삭제했다(U-7).

/**
 * 추천 실행 요청 — 대조약과 학습 경계를 **run 시점에** 싣고 간다(#58 S2).
 *
 * `referenceLotId` 를 비우면 서버가 활성 Lot 으로 폴백한다(BR-RND-04).
 * `roundIds` 가 비면 **전체 라운드 포함**이다 — 기본이 전체 포함·빼기이므로
 * "아무것도 안 골랐다" 를 "아무것도 안 쓴다" 로 읽으면 안 된다(BR-RND-07).
 */
export interface OptimizeOptions {
  referenceLotId?: string | null
  roundIds?: string[]
  excludedBatchIds?: string[]
  /**
   * 격자 **계산 제외**(#76, M901-29) — 이번 run 의 계산에서 뺄 시점(분)·용출액(pH). 삭제가 아니다.
   * 둘 다 생략하면 서버가 직전 optimization round 의 제외를 잇는다(Q-260902-07 B). 빈 목록은 해제다.
   */
  excludedTimePoints?: number[]
  excludedSolutions?: string[]
}

/**
 * 추천 **작업 등록**(202) — 요청 안에서 계산하지 않는다(BR-OPT-10). 422·409 는 `optimizeErrorDetail()`
 * 로 사유 객체를 꺼낸다. 409 의 `job_id` 는 진행 중인 작업이다 — 화면은 오류가 아니라 그 작업에 붙는다.
 */
export async function requestOptimize(
  projectId: string,
  nRecs: number,
  opts: OptimizeOptions = {},
): Promise<OptimizeAccepted> {
  const { data } = await client.post<ApiResponse<OptimizeAccepted>>(
    `/step4/${projectId}/optimize`,
    {
      n_recommendations: nRecs,
      reference_lot_id: opts.referenceLotId ?? null,
      round_ids: opts.roundIds ?? [],
      excluded_batch_ids: opts.excludedBatchIds ?? [],
      // 생략(undefined)은 JSON 에서 빠져 서버의 «이월» 로 읽힌다 — 화면은 항상 명시해 보낸다
      excluded_time_points: opts.excludedTimePoints,
      excluded_solutions: opts.excludedSolutions,
    },
  )
  return data.data!
}

/** `optimize` 422·409 의 사유 객체. 문자열 detail(종전 형식)이면 message 로 감싼다. 없으면 null. */
export function optimizeErrorDetail(e: unknown): (OptimizeErrorDetail & { status: number }) | null {
  if (!isAxiosError(e) || !e.response) return null
  const detail = (e.response.data as { detail?: unknown } | undefined)?.detail
  if (detail && typeof detail === 'object' && 'code' in detail) {
    return { ...(detail as OptimizeErrorDetail), status: e.response.status }
  }
  if (typeof detail === 'string') return { code: 'error', message: detail, status: e.response.status }
  return null
}

/**
 * 추천 작업 중단(Break, #83). `cancelled` = 대기·진행 중이던 작업을 닫았다(라운드·배치가 생기지 않는다).
 * `applying` = 계산이 끝나 반영 중이라 중단하지 않았다 — 곧 `done` 이 온다. 그 밖에는 이미 끝난 작업의 상태다.
 */
export async function cancelOptimizationJob(projectId: string, jobId: string): Promise<string> {
  const { data } = await client.post<ApiResponse<{ status: string }>>(`/step4/${projectId}/jobs/${jobId}/cancel`)
  return data.data!.status
}

export async function getOptimizationJob(projectId: string, jobId: string): Promise<OptimizationJobView> {
  const { data } = await client.get<ApiResponse<OptimizationJobView>>(`/step4/${projectId}/jobs/${jobId}`)
  return data.data!
}

/**
 * 작업 진행 스트림(SSE, BR-OPT-13). `done`·`error` 뒤 서버가 닫는다. EventSource 는 Authorization 헤더를
 * 못 실어 fetch ReadableStream 으로 파싱한다(Step 1 `searchStep1Stream` 과 같은 방식).
 * 끊기면 거부된다 — 작업은 계속되므로 호출부가 `getOptimizationJob` 으로 확인하고 다시 붙는다.
 */
export async function streamOptimizationJob(
  projectId: string,
  jobId: string,
  onEvent: (ev: OptimizationStreamEvent) => void,
  signal?: AbortSignal,
): Promise<void> {
  const token = localStorage.getItem('access_token')
  const resp = await fetch(`/api/v1/step4/${projectId}/jobs/${jobId}/stream`, {
    credentials: 'include',
    headers: token ? { Authorization: `Bearer ${token}` } : {},
    signal,
  })
  if (!resp.ok || !resp.body) {
    throw new Error(`optimization stream failed: HTTP ${resp.status}`)
  }
  const reader = resp.body.getReader()
  const decoder = new TextDecoder()
  let buffer = ''
  for (;;) {
    const { done, value } = await reader.read()
    if (done) break
    buffer += decoder.decode(value, { stream: true })
    let sep: number
    while ((sep = buffer.indexOf('\n\n')) !== -1) {
      const raw = buffer.slice(0, sep)
      buffer = buffer.slice(sep + 2)
      const line = raw.split('\n').find((l) => l.startsWith('data:'))
      if (!line) continue
      try {
        onEvent(JSON.parse(line.slice(5).trim()) as OptimizationStreamEvent)
      } catch {
        // 부분/비정상 청크는 무시
      }
    }
  }
}

/** 분석 조회(BR-OPT-16·17). 조회도 반영 계기다 — 서버가 끊긴 작업을 정리한다. */
export async function getStep4Analysis(projectId: string): Promise<Step4AnalysisView> {
  const { data } = await client.get<ApiResponse<Step4AnalysisView>>(`/step4/${projectId}/analysis`)
  return data.data!
}

// 2026-07-21 - 이슈 #43: 회차 단위 excludeRun 폐기. 포함/제외는 배치 단위이며
// init·opti에 동일 적용된다(BR-S4-L02~L04) → api/lot.ts `setBatchExcluded`.

// 2026-07-20 - 이슈 #42: opti 배치 용출 그리드 저장. 저장 즉시 f2 재계산.
export async function saveStep4Dissolution(
  projectId: string,
  batchId: string,
  profiles: DissolutionProfile[],
): Promise<Step4Run> {
  const { data } = await client.put<ApiResponse<Step4Run>>(
    `/step4/${projectId}/batches/${batchId}/dissolution`,
    { dissolution_profiles: profiles },
  )
  return data.data!
}

/**
 * 최적화 완료 — **최종 처방(배치)을 함께 확정한다**(M84-36).
 *
 * `finalBatchId` 는 필수다. 서버도 유효성을 다시 본다(제외 배치·대조약은 422) —
 * 화면이 목록에서 빼도 계약이 느슨하면 요건이 무력화된다.
 */
export async function completeOptimization(
  projectId: string,
  finalBatchId: string,
): Promise<{
  is_completed: boolean; completed_at: string; report_id: string
  final_batch_id: string; final_batch_code: string
}> {
  const { data } = await client.post<ApiResponse<{
    is_completed: boolean; completed_at: string; report_id: string
    final_batch_id: string; final_batch_code: string
  }>>(`/step4/${projectId}/complete`, { final_batch_id: finalBatchId })
  return data.data!
}
